import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import Header from '../Common/Header';
import Sidebar from '../Common/Sidebar';
import Footer from '../Common/Footer';
import { GMCContext } from '../../GMCContext';
import {
    getTicketMessages,
    updateTicketStatus,
    getTickets,
    sendMessage
} from '../../supportService';
import {
    FaArrowLeft,
    FaPaperPlane,
    FaUser,
    FaUserShield,
    FaImage,
    FaTimes
} from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function TicketChat() {
    const { id } = useParams();
    const { isDarkMode, formatDateTime, formatAddress } = useContext(GMCContext);

    const [messages, setMessages] = useState([]);
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // 🔑 Universal ApiResponse unwrap helper
    const unwrap = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        if (Array.isArray(res?.data)) return res.data;
        return [];
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [msgsRes, ticketsRes] = await Promise.all([
                getTicketMessages(id),
                getTickets()
            ]);

            // ✅ Messages
            const msgs = unwrap(msgsRes);
            setMessages(msgs);
            console.log('msgs', msgs);

            // ✅ Tickets
            const allTickets = unwrap(ticketsRes?.tickets);

            const currentTicket = allTickets.find(
                t => String(t.ticketId) === String(id)
            );
            console.log("ID FROM PARAM:", id);
            console.log("ticketsRes RAW:", ticketsRes);
            console.log("FOUND TICKET:", currentTicket);

            setTicket(currentTicket || null);

        } catch (error) {
            console.error('Error loading chat data', error);
            toast.error('Failed to load ticket details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();

        // 🟢 Socket.IO Connection Setup
        const backendBaseUrl = process.env.REACT_APP_BACKEND_LINK?.replace('/api/gtbs', '') || 'http://localhost:5553';
        const socket = io(backendBaseUrl);

        socket.on('connect', () => {
            // console.log('Socket connected:', socket.id);
            socket.emit('joinTicket', id); // Connect to unique ticket room
        });

        socket.on('newMessage', (newMsg) => {
            console.log('New message received via socket:', newMsg);
            setMessages((prev) => {
                // Ensure we don't duplicate the newly sent message
                const exists = prev.find(m => m._id === newMsg._id || (m.createdAt === newMsg.createdAt && m.sender === newMsg.sender));
                if (exists) return prev;
                return [...prev, newMsg];
            });
        });

        return () => {
            socket.disconnect();
            console.log('Socket disconnected on cleanup');
        };
    }, [id]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleStatusUpdate = async (newStatus) => {
        setStatusUpdating(true);
        try {
            await updateTicketStatus(id, newStatus);
            toast.success(`Ticket marked as ${newStatus}`);
            setTicket(prev => prev ? { ...prev, status: newStatus } : prev);
        } catch (error) {
            console.error('Failed to update status', error);
            toast.error('Failed to update status');
        } finally {
            setStatusUpdating(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const getImageUrl = (img) => {
        if (!img) return "";
        if (img.startsWith("data:") || img.startsWith("http")) return img;
        const baseURL = process.env.REACT_APP_BACKEND_LINK ? process.env.REACT_APP_BACKEND_LINK.split("/api")[0] : "";
        const cleanImg = img.startsWith("/") ? img : `/${img}`;
        return `${baseURL}${cleanImg}`;
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() && !imagePreview) return;

        setSending(true);
        try {
            // Using imagePreview as a Base64 string directly to avoid storing in tickets folder
            const attachedImage = imagePreview || "";

            const res = await sendMessage(id, newMessage, attachedImage);
            // socket will handle the real-time update, but we can also set it locally if needed
            // However, the socket listener is active, so we just clear inputs.

            setNewMessage('');
            removeImage();

            if (ticket?.status !== 'in-progress') {
                setTicket(prev => prev ? { ...prev, status: 'in-progress' } : prev);
            }

            toast.success('Message sent');

        } catch (error) {
            console.error('Failed to send message', error);
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'open': return 'bg-success';
            case 'in-progress': return 'bg-warning text-dark';
            case 'closed': return 'bg-secondary';
            default: return 'bg-light text-dark';
        }
    };
    // console.log('ticket', ticket);

    return (
        <>
            <Header />
            <Sidebar />

            <div
                className={`main-content ${isDarkMode ? 'bg-dark text-light' : 'bg-light'}`}
                style={{ marginTop: 70, minHeight: 'calc(100vh - 120px)', padding: 30 }}
            >
                {/* Header */}
                <div className="card shadow-sm mb-3">
                    <div className="card-body d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <Link to="/support" className="btn btn-light btn-sm rounded-circle">
                                <FaArrowLeft />
                            </Link>

                            <div>
                                <h5 className="fw-bold mb-1">
                                    Topic: {ticket?.subject || 'Support Ticket'}
                                    <span className="text-muted ms-2">
                                        {ticket?.ticketId}
                                    </span>
                                </h5>

                                {/* <div className="small text-muted">
                                    User: {ticket ? formatAddress(ticket.userAddress) : 'Loading...'}
                                </div> */}
                            </div>
                        </div>
                        {/* <div className="small text-muted mt-1">
                            <div>Category: {ticket?.category || 'Loading...'}</div>
                            <div>Created At: {ticket ? formatDateTime(ticket.createdAt) : 'Loading...'}</div>
                            <div>Last Message: {ticket?.lastMessage || 'Loading...'}</div>
                        </div> */}
                        <div className="dropdown">
                            <button
                                className={`btn btn-sm dropdown-toggle ${getStatusBadgeClass(ticket?.status)}`}
                                data-bs-toggle="dropdown"
                                disabled={statusUpdating}
                            >
                                {statusUpdating ? 'Updating...' : (ticket?.status || 'Unknown')}
                            </button>

                            <ul className="dropdown-menu">
                                {['open', 'in-progress', 'closed'].map(s => (
                                    <li key={s}>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleStatusUpdate(s)}
                                        >
                                            {s}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Chat */}
                <div className="card shadow-sm">
                    <div className="card-body overflow-auto" style={{ maxHeight: 550 }}>
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border" />
                            </div>
                        ) : messages.length === 0 ? (
                            <p className="text-muted text-center">No messages found</p>
                        ) : (
                            messages.map((msg) => {
                                const isAdmin = msg.role === 'admin';

                                return (
                                    <div
                                        key={msg._id || Math.random()}
                                        className={`d-flex mb-3 ${isAdmin ? 'justify-content-end' : 'justify-content-start'}`}
                                    >
                                        {!isAdmin && (
                                            <div className="flex-shrink-0 me-2">
                                                <div className="rounded-circle bg-soft-primary d-flex align-items-center justify-content-center" style={{ width: 35, height: 35 }}>
                                                    <FaUser size={14} />
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ maxWidth: '70%' }}>
                                            <div
                                                className={`p-3 rounded-3 shadow-sm ${isAdmin
                                                    ? 'bg-primary text-white'
                                                    : (isDarkMode ? 'bg-dark border border-secondary' : 'bg-white')
                                                    }`}
                                            >
                                                {msg.image && (
                                                    <div className="mb-2">
                                                        <img
                                                            src={getImageUrl(msg.image)}
                                                            alt="attachment"
                                                            className="img-fluid rounded shadow-sm"
                                                            style={{ maxHeight: '300px', cursor: 'pointer' }}
                                                            onClick={() => {
                                                                const win = window.open();
                                                                if (win) {
                                                                    win.document.write(`<img src="${getImageUrl(msg.image)}" style="max-width:100%; height:auto;" />`);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <p className="mb-0">{msg.content}</p>
                                            </div>

                                            <div className={`small text-muted mt-1 ${isAdmin ? 'text-end' : ''}`}>
                                                {formatDateTime(msg.createdAt)}
                                                {isAdmin && msg.isSeen && (
                                                    <span className="ms-2 text-success">✓✓</span>
                                                )}
                                            </div>
                                        </div>

                                        {isAdmin && (
                                            <div className="flex-shrink-0 ms-2">
                                                <div className="rounded-circle bg-soft-success d-flex align-items-center justify-content-center" style={{ width: 35, height: 35 }}>
                                                    <FaUserShield size={14} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="card-footer">
                        {imagePreview && selectedImage && (
                            <div 
                                className="mb-2 p-2 border rounded shadow-sm d-flex align-items-center justify-content-between bg-white"
                                style={{ maxWidth: '300px' }}
                            >
                                <div className="d-flex align-items-center gap-2 overflow-hidden">
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="rounded"
                                        style={{ height: '40px', width: '40px', objectFit: 'cover' }}
                                    />
                                    <div className="text-truncate">
                                        <div className="small fw-bold text-dark text-truncate">{selectedImage.name}</div>
                                        <div className="text-muted" style={{ fontSize: '10px' }}>{(selectedImage.size / 1024).toFixed(1)} KB</div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger border-0 rounded-circle ms-2"
                                    onClick={removeImage}
                                >
                                    <FaTimes size={14} />
                                </button>
                            </div>
                        )}
                        <form onSubmit={handleSendMessage} className="d-flex gap-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => fileInputRef.current.click()}
                                disabled={sending || ticket?.status === 'closed'}
                            >
                                <FaImage />
                            </button>
                            <input
                                className="form-control"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type your reply..."
                                disabled={sending || ticket?.status === 'closed'}
                            />
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={sending || (!newMessage.trim() && !selectedImage) || ticket?.status === 'closed'}
                            >
                                {sending ? <span className="spinner-border spinner-border-sm" /> : <FaPaperPlane />}
                            </button>
                        </form>

                        {ticket?.status === 'closed' && (
                            <div className="text-center text-muted small mt-2">
                                Ticket is closed. Re-open to reply.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
}
